import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mockCategories } from '../../mockData';
import './Pages.css';

function CreateListingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isWtbForm, setIsWtbForm] = useState(false);
  const [listingType, setListingType] = useState('fixed');
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    condition: '',
    price: '',
    startingPrice: '',
    auctionDuration: '7',
    description: '',
    photos: [],
    marketPriceRange: '',
  });
  const [showImagePlaceholder, setShowImagePlaceholder] = useState(false);
  
  // Check for query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get('type');
    setIsWtbForm(typeParam === 'wtb');
    
    // Check for title from WTB request (from AI Advisor)
    const titleParam = searchParams.get('title');
    if (titleParam) {
      setFormData(prev => ({
        ...prev,
        title: decodeURIComponent(titleParam)
      }));
    }
  }, [location]);
  
  // Handle form data change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle listing type change
  const handleListingTypeChange = (type) => {
    setListingType(type);
  };
  
  // Handle AI image analysis (simulated)
  const handleAnalyzeImage = () => {
    // Mock AI analysis results
    const mockAISuggestion = {
      title: 'iPhone 12 Pro Max - 256GB Graphite',
      category: 'Mobile Phones & Gadgets',
      description: 'Excellent condition iPhone 12 Pro Max in Graphite color. 256GB storage capacity. Minor scratches on the back but screen is flawless. Battery health at 89%. Comes with original charger and box. Purchased in October 2020 and used carefully.',
      price: '4800',
      marketPriceRange: '4500-5300',
      condition: 'Fully Working'
    };
    
    // Pre-fill the form with the AI suggestion including recommended price
    setFormData(prev => ({
      ...prev,
      title: mockAISuggestion.title,
      category: mockAISuggestion.category,
      description: mockAISuggestion.description,
      price: mockAISuggestion.price,
      condition: mockAISuggestion.condition,
      marketPriceRange: mockAISuggestion.marketPriceRange // Store price range in form data
    }));
  };
  
  // Add a new function to handle WTB form submission
  const handleWtbSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title || !formData.category || !formData.condition || !formData.description) {
      alert('Please fill out all required fields');
      return;
    }
    
    // Navigate to payment gateway with WTB fee data
    navigate('/payment', {
      state: {
        paymentType: 'wtb',
        amount: 15, // HK$15 fee for WTB listing
        title: formData.title,
        returnPath: '/wtb'
      }
    });
  };
  
  // Handle auction form submission
  const handleAuctionSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title || !formData.category || !formData.condition || !formData.startingPrice || !formData.description) {
      alert('Please fill out all required fields');
      return;
    }
    
    // Navigate to payment gateway with auction fee data
    navigate('/payment', {
      state: {
        paymentType: 'auctionListing',
        amount: 15, // HK$15 fee for auction listing
        title: formData.title,
        returnPath: '/browse'
      }
    });
  };
  
  // Handle fixed-price listing submission
  const handleFixedListingSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title || !formData.category || !formData.condition || !formData.price || !formData.description) {
      alert('Please fill out all required fields');
      return;
    }
    
    // For this demo, just redirect to the listings page
    navigate('/browse', { 
      state: { 
        listingSuccess: true, 
        message: 'Your listing has been created successfully!' 
      } 
    });
  };
  
  // Combined submit handler based on listing type
  const handleListingSubmit = (e) => {
    if (listingType === 'auction') {
      handleAuctionSubmit(e);
    } else {
      handleFixedListingSubmit(e);
    }
  };
  
  return (
    <div className="page create-listing-page">
      {isWtbForm ? (
        // WTB Form
        <>
          <h1>Post a Want to Buy Request</h1>
          <p>Let others know what you're looking for. A small fee applies to post WTB requests.</p>
          
          <div className="fee-notice">
            <strong>Fee:</strong> HK$15 for 30-day listing
          </div>
          
          <form className="create-form" onSubmit={handleWtbSubmit}>
            <div className="form-group">
              <label htmlFor="wtb-title">Title</label>
              <input 
                type="text" 
                id="wtb-title" 
                name="title"
                placeholder="E.g., Looking for iPhone 13 Pro Max" 
                className="form-control"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="wtb-category">Category</label>
              <select 
                id="wtb-category" 
                name="category"
                className="form-control"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select category</option>
                {mockCategories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="wtb-condition">Desired Condition</label>
              <select 
                id="wtb-condition" 
                name="condition"
                className="form-control"
                value={formData.condition}
                onChange={handleInputChange}
              >
                <option value="">Select condition</option>
                <option value="Fully Working">Fully Working</option>
                <option value="Minor Defects">Minor Defects</option>
                <option value="For Parts/Repair Only">For Parts/Repair Only</option>
                <option value="Any Condition">Any Condition</option>
              </select>
            </div>
            
            <div className="form-group price-range-group">
              <label>Price Range (HKD)</label>
              <div className="price-range-inputs">
                <input 
                  type="number" 
                  id="min-price" 
                  name="minPrice"
                  placeholder="Min" 
                  className="form-control price-input"
                />
                <span className="price-range-separator">to</span>
                <input 
                  type="number" 
                  id="max-price" 
                  name="maxPrice"
                  placeholder="Max" 
                  className="form-control price-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="wtb-description">Description</label>
              <textarea 
                id="wtb-description" 
                name="description"
                placeholder="Describe what you're looking for in detail. Include any specific features, models, or requirements." 
                className="form-control"
                rows="6"
                value={formData.description}
                onChange={handleInputChange}
              ></textarea>
            </div>
            
            <button type="submit" className="submit-button">
              Proceed to Payment
            </button>
          </form>
        </>
      ) : (
        // Regular listing form (Sell)
        <>
          <h1>Create New Listing</h1>
          <p>List your tech item for sale or auction.</p>
          
          {/* Listing Type Tabs */}
          <div className="listing-type-tabs">
            <button 
              className={`tab-button ${listingType === 'fixed' ? 'active' : ''}`}
              onClick={() => handleListingTypeChange('fixed')}
            >
              Sell (Fixed Price)
            </button>
            <button 
              className={`tab-button ${listingType === 'auction' ? 'active' : ''}`}
              onClick={() => handleListingTypeChange('auction')}
            >
              Sell (Auction)
            </button>
          </div>
          
          {/* AI Listing Assist Section */}
          <div className="ai-assist-section">
            <h2>List Faster with AI!</h2>
            <p>Upload a photo of your item and our AI will help fill out the details for you.</p>
            
            <div className="ai-image-upload">
              <div className={`image-placeholder ${showImagePlaceholder ? 'with-image' : ''}`}>
                {showImagePlaceholder ? (
                  <div className="placeholder-image">
                    <img src="https://images.unsplash.com/photo-1591337676887-a217a6970a8a" alt="Sample upload" />
                  </div>
                ) : (
                  <div className="placeholder-text">
                    <i className="placeholder-icon">📷</i>
                    <span>Image will appear here</span>
                  </div>
                )}
              </div>
              
              <div className="ai-upload-actions">
                <button 
                  type="button" 
                  className="upload-image-button"
                  onClick={() => setShowImagePlaceholder(true)}
                >
                  Upload Image
                </button>
                
                <button 
                  type="button" 
                  className="analyze-image-button"
                  onClick={handleAnalyzeImage}
                  disabled={!showImagePlaceholder}
                >
                  Analyze Image
                </button>
              </div>
            </div>
          </div>
          
          <form className="create-form" onSubmit={handleListingSubmit}>
            {/* Common Fields */}
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input 
                type="text" 
                id="title" 
                name="title"
                placeholder="Product title" 
                className="form-control"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select 
                id="category" 
                name="category"
                className="form-control"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select category</option>
                {mockCategories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="condition">Condition</label>
              <select 
                id="condition" 
                name="condition"
                className="form-control"
                value={formData.condition}
                onChange={handleInputChange}
              >
                <option value="">Select condition</option>
                <option value="Fully Working">Fully Working</option>
                <option value="Minor Defects">Minor Defects</option>
                <option value="For Parts/Repair Only">For Parts/Repair Only</option>
              </select>
            </div>
            
            {/* Conditional Fields Based on Listing Type */}
            {listingType === 'fixed' ? (
              <div className="form-group">
                <label htmlFor="price">Price (HKD)</label>
                <input 
                  type="number" 
                  id="price" 
                  name="price"
                  placeholder="Enter price" 
                  className="form-control"
                  value={formData.price}
                  onChange={handleInputChange}
                />
                {formData.marketPriceRange && (
                  <small className="price-recommendation">
                    Recommended market price range: HK${formData.marketPriceRange}
                  </small>
                )}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="startingPrice">Starting Price (HKD)</label>
                  <input 
                    type="number" 
                    id="startingPrice" 
                    name="startingPrice"
                    placeholder="Enter starting bid price" 
                    className="form-control"
                    value={formData.startingPrice}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="auctionDuration">Auction Duration</label>
                  <select 
                    id="auctionDuration" 
                    name="auctionDuration"
                    className="form-control"
                    value={formData.auctionDuration}
                    onChange={handleInputChange}
                  >
                    <option value="3">3 days</option>
                    <option value="5">5 days</option>
                    <option value="7">7 days</option>
                    <option value="10">10 days</option>
                    <option value="15">15 days</option>
                  </select>
                </div>
                
                <div className="fee-notice">
                  <strong>Auction Fee:</strong> HK$15 for 15 days, regardless of final sale price
                </div>
              </>
            )}
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea 
                id="description" 
                name="description"
                placeholder="Describe your item in detail" 
                className="form-control"
                rows="6"
                value={formData.description}
                onChange={handleInputChange}
              ></textarea>
            </div>
            
            <div className="form-group">
              <label htmlFor="photos">Photos</label>
              <input 
                type="file" 
                id="photos" 
                name="photos"
                className="form-control-file" 
                multiple 
              />
              <small>Upload up to 6 photos. First photo will be the main image.</small>
            </div>
            
            <button type="submit" className="submit-button">
              List Item
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default CreateListingPage; 